import {
    cloneElement,
    CSSProperties,
    DragEventHandler,
    FC,
    Fragment,
    isValidElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    DragDropContextProps,
    DraggableProps,
    DraggableProvidedDragHandleProps,
    DragStart,
    DragUpdate,
    DroppableProps,
    DragDropContext as PangeaDragDropContext,
    Draggable as PangeaDraggable,
    Droppable as PangeaDroppable,
} from '@hello-pangea/dnd';
import { TbGripVertical } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import {
    dragBoundaryRectsEqual,
    PlatformDragBoundary,
    PlatformDragBoundaryInsets,
    PlatformDragBoundaryRect,
    resolvePlatformDragBoundary,
} from './boundary';
import {
    PlatformDragBoundaryContext,
    PlatformDragState,
    PlatformDragStateContext,
    usePlatformDragBoundary,
    usePlatformDragState,
} from './context';
import { decorateDropPlaceholder } from './decorateDropPlaceholder';
import { getConstrainedDragStyle, getDragPreviewStyle, getDropZoneIdleStyle, getDropZoneStyle } from './platformStyles';
import SortableInsertionIndicator from './SortableInsertionIndicator';

export { default as DropPlaceholder } from './DropPlaceholder';
export { default as SyncingSmallTag } from './SyncingSmallTag';
// eslint-disable-next-line react-refresh/only-export-components -- intentional public barrel for the DnD persistence hook
export { default as useQueuedItemPersistence } from './useQueuedItemPersistence';

interface StyleableElementProps {
    style?: CSSProperties;
    'data-platform-drop-zone'?: 'active';
}

interface PlatformDroppableProps extends DroppableProps {
    decorateActive?: boolean;
    decoratePlaceholder?: boolean;
    /**
     * `insertion` renders the platform's position-aware overlay. `measured`
     * decorates the drag engine's own placeholder, which is safer inside
     * transient popup surfaces that can close while a drag is active.
     */
    placeholderMode?: 'insertion' | 'measured';
}

export interface PlatformDragDropContextProps extends DragDropContextProps {
    /** Constrains previews to the owning DnD surface by default. */
    boundary?: PlatformDragBoundary;
    /** Reserves an optional safe inset inside the resolved boundary. */
    boundaryInsets?: number | PlatformDragBoundaryInsets;
}

export interface DragHandleProps {
    className?: string;
    disabled?: boolean;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    isDragging?: boolean;
    label: string;
    nativeDraggable?: boolean;
    onNativeDragEnd?: DragEventHandler<HTMLSpanElement>;
    onNativeDragStart?: DragEventHandler<HTMLSpanElement>;
    style?: CSSProperties;
}

const activePlatformDragContexts = new Set<symbol>();

/**
 * Cancels the active platform drag through the drag engine's keyboard sensor.
 * Popup surfaces call this before unmounting so the engine can restore global
 * pointer, focus, and body styles before its owning tree disappears.
 */
export const cancelActivePlatformDrag = (): boolean => {
    if (!activePlatformDragContexts.size || typeof window === 'undefined') {
        return false;
    }

    window.dispatchEvent(
        new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            code: 'Escape',
            key: 'Escape',
        }),
    );

    return true;
};

/** Runs a close/unmount mutation after an active drag has had a frame to cancel. */
export const runAfterPlatformDragCancellation = (callback: () => void): void => {
    if (cancelActivePlatformDrag() && typeof window !== 'undefined') {
        window.requestAnimationFrame(callback);
        return;
    }

    callback();
};

/**
 * The platform drag handle keeps the interactive target and visual language
 * consistent while allowing each drag engine to supply its own behavior.
 */
export const DragHandle: FC<DragHandleProps> = ({
    className,
    disabled = false,
    dragHandleProps,
    isDragging = false,
    label,
    nativeDraggable = false,
    onNativeDragEnd,
    onNativeDragStart,
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const interactive = !disabled && Boolean(dragHandleProps || className || nativeDraggable);

    return (
        <span
            {...(interactive ? dragHandleProps : undefined)}
            aria-label={label}
            aria-disabled={!interactive || undefined}
            className={className}
            data-platform-drag-handle
            draggable={nativeDraggable && interactive}
            onBlur={() => setFocused(false)}
            onDragEnd={nativeDraggable ? onNativeDragEnd : undefined}
            onDragStart={nativeDraggable ? onNativeDragStart : undefined}
            onFocus={() => setFocused(true)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            role={interactive ? (dragHandleProps?.role ?? 'button') : undefined}
            tabIndex={interactive ? (dragHandleProps?.tabIndex ?? 0) : -1}
            title={label}
            style={{
                alignItems: 'center',
                backgroundColor: focused || hovered ? token.colorFillTertiary : 'transparent',
                borderRadius: token.borderRadiusSM,
                boxShadow: focused ? `0 0 0 2px ${token.colorPrimaryBorder}` : undefined,
                color: token.colorTextQuaternary,
                cursor: interactive ? (isDragging ? 'grabbing' : 'grab') : 'default',
                display: 'inline-flex',
                flex: '0 0 auto',
                height: token.controlHeightSM,
                justifyContent: 'center',
                lineHeight: 1,
                minWidth: token.controlHeightSM,
                opacity: interactive ? 1 : 0.45,
                touchAction: 'none',
                transition: `background-color ${token.motionDurationFast}, color ${token.motionDurationFast}`,
                userSelect: 'none',
                ...style,
            }}
        >
            <TbGripVertical aria-hidden size='0.875rem' />
        </span>
    );
};

/**
 * Enhances the drag engine's full source element instead of constructing a
 * reduced proxy, so previews retain the exact measured width and height.
 */
export const Draggable: FC<DraggableProps> = ({ children, ...props }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const dragBoundary = usePlatformDragBoundary();

    return (
        <PangeaDraggable {...props}>
            {(provided, snapshot, rubric) =>
                children(
                    {
                        ...provided,
                        draggableProps: {
                            ...provided.draggableProps,
                            style: getDragPreviewStyle(
                                provided.draggableProps.style,
                                snapshot.isDragging,
                                token,
                                dragBoundary?.boundaryRect,
                            ),
                        },
                    },
                    snapshot,
                    rubric,
                )
            }
        </PangeaDraggable>
    );
};

/** Coordinates the platform insertion marker while preserving each caller's responders. */
export const DragDropContext: FC<PlatformDragDropContextProps> = ({
    boundary = 'context',
    boundaryInsets,
    children,
    onBeforeDragStart,
    onDragEnd,
    onDragStart,
    onDragUpdate,
    ...props
}): JSX.Element => {
    const [dragState, setDragState] = useState<PlatformDragState>();
    const [boundaryRect, setBoundaryRect] = useState<PlatformDragBoundaryRect | null>(null);
    const [sourceDroppableId, setSourceDroppableId] = useState<string>();
    const droppableElementsRef = useRef(new Map<string, HTMLElement>());
    const contextIdRef = useRef(Symbol('platform-drag-context'));
    const draggingRef = useRef(false);

    const clearDragState = useCallback(() => {
        draggingRef.current = false;
        activePlatformDragContexts.delete(contextIdRef.current);
        setDragState(undefined);
        setSourceDroppableId(undefined);
        setBoundaryRect(null);
    }, []);

    const registerDroppable = useCallback((droppableId: string, element: HTMLElement | null) => {
        if (element) {
            droppableElementsRef.current.set(droppableId, element);
        } else {
            droppableElementsRef.current.delete(droppableId);
        }
    }, []);

    const measureBoundary = useCallback(
        (sourceId: string): PlatformDragBoundaryRect | null =>
            resolvePlatformDragBoundary({
                boundary,
                droppableElements: droppableElementsRef.current,
                insets: boundaryInsets,
                sourceDroppableId: sourceId,
            }),
        [boundary, boundaryInsets],
    );

    const refreshBoundary = useCallback(
        (sourceId: string) => {
            const nextBoundary = measureBoundary(sourceId);
            setBoundaryRect((currentBoundary) =>
                dragBoundaryRectsEqual(currentBoundary, nextBoundary) ? currentBoundary : nextBoundary,
            );
        },
        [measureBoundary],
    );

    useEffect(() => {
        if (!sourceDroppableId) {
            return undefined;
        }

        const refresh = () => refreshBoundary(sourceDroppableId);
        window.addEventListener('resize', refresh);
        window.addEventListener('scroll', refresh, true);

        return () => {
            window.removeEventListener('resize', refresh);
            window.removeEventListener('scroll', refresh, true);
        };
    }, [refreshBoundary, sourceDroppableId]);

    useEffect(
        () => () => {
            if (draggingRef.current) {
                cancelActivePlatformDrag();
                activePlatformDragContexts.delete(contextIdRef.current);
                draggingRef.current = false;
            }
        },
        [],
    );

    const boundaryContextValue = useMemo(
        () => ({ boundaryRect, registerDroppable }),
        [boundaryRect, registerDroppable],
    );

    const toDragState = (update: DragStart | DragUpdate): PlatformDragState => ({
        destinationIndex: ('destination' in update ? update.destination?.index : undefined) ?? update.source.index,
        draggableId: update.draggableId,
        droppableId:
            ('destination' in update ? update.destination?.droppableId : undefined) ?? update.source.droppableId,
        sourceIndex: update.source.index,
    });

    return (
        <PlatformDragBoundaryContext.Provider value={boundaryContextValue}>
            <PlatformDragStateContext.Provider value={dragState}>
                <PangeaDragDropContext
                    {...props}
                    onBeforeDragStart={onBeforeDragStart}
                    onDragStart={(start, provided) => {
                        draggingRef.current = true;
                        activePlatformDragContexts.add(contextIdRef.current);
                        setDragState(toDragState(start));
                        setSourceDroppableId(start.source.droppableId);
                        refreshBoundary(start.source.droppableId);
                        onDragStart?.(start, provided);
                    }}
                    onDragUpdate={(update, provided) => {
                        setDragState(update.destination ? toDragState(update) : undefined);
                        refreshBoundary(update.source.droppableId);
                        onDragUpdate?.(update, provided);
                    }}
                    onDragEnd={(result, provided) => {
                        clearDragState();
                        onDragEnd(result, provided);
                    }}
                >
                    {children}
                </PangeaDragDropContext>
            </PlatformDragStateContext.Provider>
        </PlatformDragBoundaryContext.Provider>
    );
};

/**
 * Decorates the engine's measured insertion marker by default. Container-level
 * treatment is opt-in for empty boards and other targets where no neighboring
 * item exists to communicate the destination.
 */
export const Droppable: FC<PlatformDroppableProps> = ({
    children,
    decorateActive = false,
    decoratePlaceholder = true,
    placeholderMode = 'insertion',
    renderClone,
    ...props
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const dragBoundary = usePlatformDragBoundary();
    const dragState = usePlatformDragState();

    return (
        <PangeaDroppable
            {...props}
            renderClone={
                renderClone
                    ? (provided, snapshot, rubric) =>
                          renderClone(
                              {
                                  ...provided,
                                  draggableProps: {
                                      ...provided.draggableProps,
                                      style: getConstrainedDragStyle(
                                          provided.draggableProps.style,
                                          dragBoundary?.boundaryRect,
                                      ),
                                  },
                              },
                              snapshot,
                              rubric,
                          )
                    : null
            }
        >
            {(provided, snapshot) => {
                const platformProvided = {
                    ...provided,
                    innerRef: (element?: HTMLElement | null) => {
                        provided.innerRef(element);
                        dragBoundary?.registerDroppable(props.droppableId, element ?? null);
                    },
                    placeholder:
                        decoratePlaceholder && placeholderMode === 'measured'
                            ? decorateDropPlaceholder(provided.placeholder, token)
                            : provided.placeholder,
                };
                const rendered = children(platformProvided, snapshot);
                const showInsertionIndicator =
                    decoratePlaceholder &&
                    placeholderMode === 'insertion' &&
                    snapshot.isDraggingOver &&
                    dragState?.droppableId === props.droppableId &&
                    dragState.draggableId &&
                    dragState.destinationIndex !== dragState.sourceIndex;
                const content = showInsertionIndicator ? (
                    <>
                        {rendered}
                        <SortableInsertionIndicator
                            destinationIndex={dragState.destinationIndex}
                            direction={props.direction ?? 'vertical'}
                            draggableId={dragState.draggableId}
                            droppableId={props.droppableId}
                        />
                    </>
                ) : (
                    rendered
                );

                if (!decorateActive || !isValidElement<StyleableElementProps>(content)) {
                    return content;
                }

                if (content.type === Fragment) {
                    return content;
                }

                return cloneElement(content, {
                    'data-platform-drop-zone': snapshot.isDraggingOver ? 'active' : undefined,
                    style: snapshot.isDraggingOver
                        ? getDropZoneStyle(content.props.style, token)
                        : getDropZoneIdleStyle(content.props.style, token),
                });
            }}
        </PangeaDroppable>
    );
};

export type {
    DraggableLocation,
    DraggableProvided,
    DraggableProvidedDraggableProps,
    DraggableProvidedDragHandleProps,
    DraggableStateSnapshot,
    DraggableStyle,
    DragStart,
    DragUpdate,
    DropResult,
} from '@hello-pangea/dnd';
export type { DragAndDropDesignTokens } from './platformStyles';
export type {
    PlatformDragBoundary,
    PlatformDragBoundaryInsets,
    PlatformDragBoundaryRect,
    PlatformDragBoundaryResolver,
    PlatformDragBoundaryResolverContext,
} from './boundary';
